import {
  type Announcements,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type KeyboardCoordinateGetter,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { Button, cx, ErrorState, FullPageSpinner, Icon, useToast } from "@/components/ui";
import { portal } from "@/lib/api";
import { findNiche, growthKey, useGrowthBoard, useGrowthNiches, useGrowthProfile } from "@/lib/growth";
import { errorMessage } from "@/lib/http";
import type { GrowthBoard, GrowthNiche, GrowthTask, GrowthTaskStatus, User } from "@/lib/types";

import { boardQuery, PHASES, pipelineFilters, TaskCard, usePeople } from "./dashboard/board";
import { Breadcrumb, pillButton, taskPath, WidgetError, WidgetLoading } from "./dashboard/ui";
import { FilterBar, useFilterValues } from "./filters";
import { ASKED, type AskedMove, MoveDialog } from "./move-dialog";
import { movable, moveFor, movedOnBoard, type Mover, searchBoard, withTask } from "./pipeline";
import { RevisionDialog } from "./revision-dialog";
import { TaskDialog } from "./task-dialog";

const ORDER = PHASES.map((p) => p.id);
const PHASE_LABEL = Object.fromEntries(PHASES.map((p) => [p.id, p.label])) as Record<
  GrowthTaskStatus,
  string
>;

/**
 * Recommendations (Figma 4248:2): the whole task board, every card in its phase's column under the filter
 * bar (kept in the URL), with "Create task" for work that doesn't start from an insight. Cards are dragged
 * between columns to move them through the pipeline. Opened from the dashboard's "Recommended actions ·
 * View more".
 */
export function RecommendationsPage() {
  const profile = useGrowthProfile();
  const niches = useGrowthNiches();

  if (profile.isPending) return <FullPageSpinner />;
  if (profile.isError) return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  if (profile.data.status !== "active") return <Navigate to="/growth-intelligence" replace />;
  return <Recommendations niche={findNiche(niches.data, profile.data.niche_id)} />;
}

function Recommendations({ niche }: { niche: GrowthNiche | undefined }) {
  const specs = pipelineFilters(niche);
  const values = useFilterValues(specs);
  const board = useGrowthBoard(boardQuery(values));
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  // the search narrows the cards the filters fetched (kept in the URL with them)
  const search = params.get(SEARCH_KEY) ?? "";
  const shown = useMemo(
    () => (board.data ? searchBoard(board.data, search) : undefined),
    [board.data, search],
  );
  const filtered = specs.some((s) => (values[s.key] ?? []).some((v) => v !== s.initial));
  const total = shown ? Object.values(shown.counts).reduce((a, b) => a + b, 0) : 0;
  const setSearch = (value: string) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (value) out.set(SEARCH_KEY, value);
        else out.delete(SEARCH_KEY);
        return out;
      },
      { replace: true },
    );
  const reset = () =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        for (const s of specs) out.delete(s.key);
        return out;
      },
      { replace: true },
    );

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumb current="Recommendations" />

      <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
        <h1 className="min-w-0 flex-1 text-2xl font-bold text-fg">Recommendations</h1>
        <Button
          unstyled
          requires="growth:tasks_manage"
          onClick={() => setCreating(true)}
          className={pillButton}
        >
          Create task
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <BoardSearch value={search} onChange={setSearch} />
        <div className="min-w-0 flex-1">
          <FilterBar label="Filter recommendations" specs={specs} />
        </div>
      </div>

      {board.isPending ? (
        <WidgetLoading label="Loading recommendations" />
      ) : board.isError || !shown ? (
        <WidgetError error={board.error} onRetry={() => void board.refetch()} />
      ) : (
        <>
          <PipelineBoard board={shown} />
          {total === 0 && search.trim() ? (
            <div className="flex flex-col items-center gap-1.5 rounded-[14px] border border-dashed border-border px-6 py-12 text-center">
              <p className="text-sm font-semibold text-fg">No tasks match “{search.trim()}”</p>
              <p className="max-w-md text-[13px] text-fg-muted">
                Search looks in titles, descriptions and the insight a task came from, or a ticket id.
              </p>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-2 text-[12.5px] font-semibold text-brand-500 hover:text-brand-600"
              >
                Clear search
              </button>
            </div>
          ) : (
            total === 0 && (
              <div className="flex flex-col items-center gap-1.5 rounded-[14px] border border-dashed border-border px-6 py-12 text-center">
                <p className="text-sm font-semibold text-fg">
                  {filtered ? "No tasks match these filters" : "No tasks yet"}
                </p>
                <p className="max-w-md text-[13px] text-fg-muted">
                  {filtered
                    ? "Try another topic, urgency or status, or reset the filters."
                    : "Open an insight and create a task from it, or use Create task. It appears here and moves from To Do to Live."}
                </p>
                {filtered && (
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-2 text-[12.5px] font-semibold text-brand-500 hover:text-brand-600"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            )
          )}
        </>
      )}

      {creating && <TaskDialog onClose={() => setCreating(false)} boardLink={false} />}
    </div>
  );
}

/** The search's place in the URL, beside the filters (`?q=applicant`). */
const SEARCH_KEY = "q";

/** Find a card by its words or ticket id; shaped like the filter pills beside it. */
function BoardSearch({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="inline-flex w-full items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-[7px] text-[12.5px] text-fg-muted focus-within:border-fg-subtle sm:w-[260px]">
      <Icon name="search" size={14} className="shrink-0" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && value) onChange("");
        }}
        placeholder="Search tasks or ticket id"
        aria-label="Search recommendations"
        className="min-w-0 flex-1 bg-transparent text-fg outline-none placeholder:text-fg-subtle [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="shrink-0 rounded-full text-[12.5px] leading-none text-fg-subtle hover:text-fg"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// ── the board, with its cards dragged between columns ──────────────────────────

/** Where a column stands while a card is dragged: a place it can go, can't go, or where it came from. */
type ColumnState = "idle" | "open" | "closed" | "home";

/** The mouse picks a column by the pointer; the keyboard (no pointer) by where the card overlaps most. */
const collisions: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : rectIntersection(args);
};

/**
 * The arrow keys carry a picked-up card a whole column at a time, stopping only at columns it can go to
 * (and the one it came from), in the board's order.
 */
function columnKeys(stops: (taskId: string) => GrowthTaskStatus[]): KeyboardCoordinateGetter {
  return (event, { context: { active, over, droppableRects, collisionRect } }) => {
    const step = ["ArrowRight", "ArrowDown"].includes(event.code)
      ? 1
      : ["ArrowLeft", "ArrowUp"].includes(event.code)
        ? -1
        : 0;
    if (!step || !active || !collisionRect) return undefined;
    event.preventDefault();
    const order = stops(String(active.id));
    const home = (active.data.current as { status?: GrowthTaskStatus } | undefined)?.status;
    const here = order.indexOf((over?.id as GrowthTaskStatus | undefined) ?? home ?? order[0]!);
    const next = order[(here < 0 ? order.indexOf(home!) : here) + step];
    const rect = next ? droppableRects.get(next) : undefined;
    if (!rect) return undefined;
    return { x: rect.left + (rect.width - collisionRect.width) / 2, y: rect.top + 8 };
  };
}

function PipelineBoard({ board }: { board: GrowthBoard }) {
  const { can } = useOrganization();
  const user = useUser();
  const people = usePeople();
  const move = useMoveTask();
  const who: Mover = { userId: user.id, can };
  const [dragging, setDragging] = useState<GrowthTask | null>(null);
  const [asking, setAsking] = useState<{ task: GrowthTask; to: GrowthTaskStatus; kind: AskedMove } | null>(
    null,
  );
  const find = (id: unknown) => board.items.find((t) => t.id === id);
  const stops = (taskId: string) => {
    const task = find(taskId);
    return task ? ORDER.filter((to) => to === task.status || moveFor(task, to, who) !== null) : [];
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // a short press first, so a finger on a card can still scroll the page
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    // Space picks a card up and drops it; Enter is left to open the card
    useSensor(KeyboardSensor, {
      coordinateGetter: columnKeys(stops),
      keyboardCodes: { start: ["Space"], end: ["Space"], cancel: ["Escape"] },
    }),
  );

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const task = find(active.id);
      return task ? `Picked up ${task.title}, in ${PHASE_LABEL[task.status]}.` : undefined;
    },
    onDragOver: ({ active, over }) => {
      const task = find(active.id);
      if (!task) return undefined;
      if (!over) return `${task.title} isn't over a column.`;
      const to = over.id as GrowthTaskStatus;
      return to === task.status ? `Back in ${PHASE_LABEL[to]}.` : `Over ${PHASE_LABEL[to]}.`;
    },
    onDragEnd: ({ active, over }) => {
      const task = find(active.id);
      if (!task) return undefined;
      const to = over?.id as GrowthTaskStatus | undefined;
      return to && moveFor(task, to, who)
        ? `Dropped ${task.title} in ${PHASE_LABEL[to]}.`
        : `${task.title} stays in ${PHASE_LABEL[task.status]}.`;
    },
    onDragCancel: ({ active }) => {
      const task = find(active.id);
      return task ? `Cancelled. ${task.title} stays in ${PHASE_LABEL[task.status]}.` : undefined;
    },
  };

  function onDragEnd({ active, over }: DragEndEvent) {
    setDragging(null);
    const task = find(active.id);
    const to = over?.id as GrowthTaskStatus | undefined;
    if (!task || !to) return;
    const kind = moveFor(task, to, who);
    if (!kind) return;
    if (ASKED.has(kind)) setAsking({ task, to, kind: kind as AskedMove });
    else move.mutate({ task, to });
  }

  const stateOf = (phase: GrowthTaskStatus): ColumnState => {
    if (!dragging) return "idle";
    if (phase === dragging.status) return "home";
    return moveFor(dragging, phase, who) ? "open" : "closed";
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisions}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            "Press Enter to open a card. To move it, press Space to pick it up, use the arrow keys to choose a column, then press Space to drop it. Press Escape to cancel.",
        },
      }}
      onDragStart={({ active }) => setDragging(find(active.id) ?? null)}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {PHASES.map((phase) => (
          <Column key={phase.id} phase={phase} count={board.counts[phase.id]} state={stateOf(phase.id)}>
            {board.items
              .filter((t) => t.status === phase.id)
              .map((task) => (
                <DraggableCard
                  key={task.id}
                  task={task}
                  people={people}
                  canMove={movable(task, who, ORDER)}
                />
              ))}
          </Column>
        ))}
      </div>
      {asking?.kind === "revise" && (
        <RevisionDialog
          onCancel={() => setAsking(null)}
          onSend={(message, reasons) => {
            move.mutate({ task: asking.task, to: asking.to, message, reasons });
            setAsking(null);
          }}
        />
      )}
      {asking && asking.kind !== "revise" && (
        <MoveDialog
          task={asking.task}
          kind={asking.kind}
          onCancel={() => setAsking(null)}
          onConfirm={(note) => {
            move.mutate({
              task: asking.task,
              to: asking.to,
              reason: asking.kind === "reject" ? note : undefined,
            });
            setAsking(null);
          }}
        />
      )}
    </DndContext>
  );
}

function Column({
  phase,
  count,
  state,
  children,
}: {
  phase: (typeof PHASES)[number];
  count: number;
  state: ColumnState;
  children: ReactNode;
}) {
  // the column a card came from takes it back (a drop there changes nothing)
  const { setNodeRef, isOver } = useDroppable({ id: phase.id, disabled: state === "closed" });
  return (
    <section aria-label={phase.label} className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <h2 className="flex items-center gap-1.5 text-[11.5px] text-fg-subtle">
          <span className="font-semibold tracking-[0.575px] uppercase">{phase.label}</span>{" "}
          <span>{count}</span>
        </h2>
        <span aria-hidden className={cx("h-[3px] rounded-[2px]", phase.bar)} />
      </div>
      <div
        ref={setNodeRef}
        data-phase={phase.id}
        className={cx(
          "flex min-h-[120px] flex-1 flex-col gap-3 rounded-xl transition-[background-color,opacity,outline-color]",
          state === "open" && "outline-[1.5px] outline-offset-4 outline-brand-500/40 outline-dashed",
          state === "open" && isOver && "bg-brand-50 outline-brand-500",
          state === "closed" && "opacity-45",
        )}
      >
        {children}
      </div>
    </section>
  );
}

function DraggableCard({
  task,
  people,
  canMove,
}: {
  task: GrowthTask;
  people: Map<string, User>;
  canMove: boolean;
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: task.id,
    data: { status: task.status },
    disabled: !canMove,
  });
  // a card that can't move is simply a link to its detail
  if (!canMove) {
    return (
      <Link
        to={taskPath(task.id)}
        aria-label={task.title}
        className="rounded-xl transition-shadow hover:shadow-[0_4px_12px_rgba(51,31,20,0.08)] focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:outline-none"
      >
        <TaskCard task={task} people={people} />
      </Link>
    );
  }
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      // a click that didn't drag (the mouse moved less than 5px) opens the card; so does Enter
      onClick={() => navigate(taskPath(task.id))}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(taskPath(task.id));
        else listeners?.onKeyDown?.(e);
      }}
      aria-label={task.title}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cx(
        "cursor-grab rounded-xl transition-shadow hover:shadow-[0_4px_12px_rgba(51,31,20,0.08)] focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:outline-none",
        // lifted above the other columns while it travels across them
        isDragging && "relative z-10 cursor-grabbing shadow-[0_12px_24px_rgba(51,31,20,0.16)]",
      )}
    >
      <TaskCard task={task} people={people} />
    </div>
  );
}

// ── moving a card ────────────────────────────────────────────────────────────

interface MoveInput {
  task: GrowthTask;
  to: GrowthTaskStatus;
  message?: string;
  reason?: string;
  reasons?: string[];
}

/**
 * Moves a card through the pipeline. It moves on screen at once (every board view of this business), and
 * goes back where it was if the API says no, with the reason.
 */
function useMoveTask() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const boards = growthKey(orgId, "board");
  return useMutation({
    mutationFn: ({ task, to, message, reason, reasons }: MoveInput) =>
      portal.moveTask(orgId, task.id, to, { version: task.version, message, reason, reasons }),
    onMutate: async ({ task, to }) => {
      await qc.cancelQueries({ queryKey: boards });
      const before = qc.getQueriesData<GrowthBoard>({ queryKey: boards });
      qc.setQueriesData<GrowthBoard>({ queryKey: boards }, (b) => (b ? movedOnBoard(b, task.id, to) : b));
      return { before };
    },
    // the API's copy carries the card's new version, so a second move straight after is accepted
    onSuccess: (saved) =>
      qc.setQueriesData<GrowthBoard>({ queryKey: boards }, (b) => (b ? withTask(b, saved) : b)),
    onError: (err, { task }, context) => {
      for (const [key, data] of context?.before ?? []) qc.setQueryData(key, data);
      toast.show({ tone: "warning", title: `“${task.title}” didn't move`, body: errorMessage(err) });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: boards }),
  });
}
